// =============================================================================
// components/CustomerMiniDrawer.js — Müşteri Mini 360 Drawer (Faz BF)
// =============================================================================
// Sağdan kayan dar panel: müşteri profil özet + son 5 sipariş + risk flag'leri.
// Orders listesinde müşteri adına tıklayınca açılır — sayfa terk etmeden bakış.
// "Tam 360 Aç" butonu ile /customer/:id sayfasına navigate.
// =============================================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer, Box, Typography, Stack, Chip, Divider, CircularProgress, Alert,
  List, ListItem, ListItemText, IconButton, Button, Avatar, Tooltip,
  TextField, MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
// Faz BJ (2026-05-10): Notes UI iconları
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArchiveIcon from "@mui/icons-material/Archive";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";

const API_BASE = "https://api.miksanplastik.com.tr";

function fmt(amount) {
  return '₺' + (Number(amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_COLOR = {
  pending: 'default', paid_pending_approval: 'warning', confirmed: 'success',
  preparing: 'info', shipped: 'primary', delivered: 'success',
  cancelled: 'error', rejected: 'error'
};

// Faz BJ: Not rengi → MUI Alert severity + arkaplan
const NOTE_COLORS = {
  yellow: { bg: '#fef9c3', border: '#facc15', label: 'Sarı (genel)' },
  green:  { bg: '#dcfce7', border: '#22c55e', label: 'Yeşil (olumlu)' },
  red:    { bg: '#fee2e2', border: '#ef4444', label: 'Kırmızı (acil)' },
  blue:   { bg: '#dbeafe', border: '#3b82f6', label: 'Mavi (bilgi)' },
  purple: { bg: '#f3e8ff', border: '#a855f7', label: 'Mor (özel)' }
};

export default function CustomerMiniDrawer({ userId, open, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Faz BJ: notlar state
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newNoteColor, setNewNoteColor] = useState("yellow");
  const [savingNote, setSavingNote] = useState(false);

  // Notları çek (drawer açıldığında veya userId değişince)
  const loadNotes = (uid) => {
    setNotesLoading(true);
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/admin/notes?scope=customer&scope_id=${uid}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setNotes(d.items || []))
      .catch(() => setNotes([]))
      .finally(() => setNotesLoading(false));
  };

  // Yeni not ekle
  const addNote = async () => {
    const body = newNote.trim();
    if (!body || savingNote) return;
    setSavingNote(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/admin/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scope: 'customer', scope_id: userId, body, color: newNoteColor })
      });
      if (res.ok) {
        setNewNote("");
        loadNotes(userId);
      }
    } finally { setSavingNote(false); }
  };

  // Status değiştir (active → done → archived)
  const updateNoteStatus = async (noteId, status) => {
    const token = localStorage.getItem("token");
    await fetch(`${API_BASE}/api/admin/notes/${noteId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    loadNotes(userId);
  };

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true); setError(null); setData(null); setNotes([]);
    const token = localStorage.getItem("token");
    fetch(`${API_BASE}/api/admin/users/${userId}/profile-360`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    loadNotes(userId);  // paralel — sonuç gelmeden bile drawer açılır
  }, [open, userId]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420 } }}>
      <Box sx={{ p: 2, bgcolor: '#1565C0', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={700}>👤 Müşteri Detay</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

      {data && (
        <Box sx={{ p: 2 }}>
          {/* Profil */}
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: '#1565C0', width: 56, height: 56 }}>
              {(data.profile.name || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography fontWeight={700}>{data.profile.company_name || data.profile.name}</Typography>
              {data.profile.company_name && (
                <Typography variant="caption" color="text.secondary">{data.profile.name}</Typography>
              )}
              <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={data.profile.role} variant="outlined" />
                {data.profile.has_vkn && <Chip size="small" label="VKN" color="primary" variant="outlined" />}
                {data.profile.status !== 'active' && <Chip size="small" label={data.profile.status} color="warning" />}
              </Box>
            </Box>
          </Stack>

          {/* İletişim */}
          <Stack spacing={0.5} sx={{ fontSize: 13, mb: 2 }}>
            {data.profile.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon fontSize="small" color="action" />
                <a href={`tel:${data.profile.phone}`} style={{ color: '#1565C0', textDecoration: 'none' }}>{data.profile.phone}</a>
              </Box>
            )}
            {data.profile.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <a href={`mailto:${data.profile.email}`} style={{ color: '#1565C0', textDecoration: 'none' }}>{data.profile.email}</a>
              </Box>
            )}
            {data.profile.tax_number && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" color="action" />
                <Typography variant="caption">VKN {data.profile.tax_number} {data.profile.tax_office ? '· ' + data.profile.tax_office : ''}</Typography>
              </Box>
            )}
          </Stack>

          {/* Flag uyarıları */}
          {data.flags.map((f, i) => (
            <Alert key={i} severity={f.level} sx={{ mb: 1, py: 0.5 }}>{f.text}</Alert>
          ))}

          <Divider sx={{ my: 2 }} />

          {/* Faz BJ: Notes section — telefon konuşması özetleri + iç notlar */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>
              <StickyNote2Icon fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
              Notlar ({notes.filter(n => n.status === 'active').length} aktif)
            </Typography>
          </Stack>

          {/* Yeni not ekleme inline form */}
          <Box sx={{ mb: 1.5 }}>
            <TextField
              size="small" fullWidth multiline rows={2}
              placeholder="Telefon konuşması özeti, hatırlatma…"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              disabled={savingNote}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addNote(); }}
            />
            <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
              <TextField
                select size="small" value={newNoteColor}
                onChange={(e) => setNewNoteColor(e.target.value)}
                sx={{ width: 130 }}
              >
                {Object.entries(NOTE_COLORS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    <Box component="span" sx={{ display: 'inline-block', width: 12, height: 12, bgcolor: v.border, borderRadius: '50%', mr: 1, verticalAlign: 'middle' }} />
                    {v.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button size="small" variant="contained" startIcon={savingNote ? <CircularProgress size={14} /> : <AddIcon />}
                      onClick={addNote} disabled={savingNote || !newNote.trim()}>
                Ekle
              </Button>
              <Typography variant="caption" color="text.secondary">Ctrl+Enter</Typography>
            </Stack>
          </Box>

          {/* Notes listesi — aktif önce, done/archived altta soluk */}
          {notesLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={16} /></Box>}
          <Stack spacing={0.5} sx={{ maxHeight: 240, overflow: 'auto' }}>
            {notes.length === 0 && !notesLoading && (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', p: 1 }}>
                Henüz not yok — yukarıdan ekle
              </Typography>
            )}
            {notes.map(n => {
              const c = NOTE_COLORS[n.color] || NOTE_COLORS.yellow;
              const isDone = n.status === 'done';
              return (
                <Box key={n.id} sx={{
                  p: 1, bgcolor: c.bg, borderLeft: `4px solid ${c.border}`,
                  borderRadius: 1, opacity: isDone ? 0.55 : 1,
                  textDecoration: isDone ? 'line-through' : 'none'
                }}>
                  <Typography fontSize={12.5} sx={{ whiteSpace: 'pre-wrap' }}>{n.body}</Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {n.author_name || 'Admin'} · {new Date(n.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    <Stack direction="row" spacing={0.25}>
                      {!isDone && (
                        <Tooltip title="Yapıldı işaretle">
                          <IconButton size="small" onClick={() => updateNoteStatus(n.id, 'done')}>
                            <CheckCircleIcon fontSize="small" sx={{ fontSize: 16, color: '#10b981' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Arşivle (gizle)">
                        <IconButton size="small" onClick={() => updateNoteStatus(n.id, 'archived')}>
                          <ArchiveIcon fontSize="small" sx={{ fontSize: 16, color: '#64748b' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Stats grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
            <StatCard label="Lifetime ciro" value={fmt(data.stats.lifetime_revenue)} subtitle={`${data.stats.lifetime_confirmed} sipariş`} color="#1565C0" />
            <StatCard label="Son 90 gün" value={fmt(data.stats.revenue_90d)} subtitle={`${data.stats.orders_90d} sipariş`} color="#10B981" />
            <StatCard label="Ortalama" value={fmt(data.stats.avg_order_value)} color="#7C3AED" />
            <StatCard label="Bekleyen" value={fmt(data.stats.pending_amount)} subtitle={`${data.stats.pending_count} sipariş`} color="#F59E0B" />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Son siparişler */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>
            📦 Son Siparişler ({data.recent_orders.length})
          </Typography>
          <List dense disablePadding sx={{ maxHeight: 240, overflow: 'auto', mt: 0.5 }}>
            {data.recent_orders.slice(0, 10).map(o => (
              <ListItem key={o.id} sx={{ borderBottom: '1px solid #f1f5f9', px: 0 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontSize={13} fontWeight={600}>#{o.id} · {fmt(o.total_price)}</Typography>
                      <Chip size="small" label={o.status} color={STATUS_COLOR[o.status] || 'default'} sx={{ height: 18, fontSize: 10 }} />
                    </Stack>
                  }
                  secondary={fmtDate(o.created_at)}
                />
              </ListItem>
            ))}
          </List>

          {data.top_products.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>
                ⭐ En Çok Aldığı Ürünler
              </Typography>
              <List dense disablePadding sx={{ mt: 0.5 }}>
                {data.top_products.map(p => (
                  <ListItem key={p.id} sx={{ px: 0 }}>
                    <ListItemText
                      primary={<Typography fontSize={13}>{p.name}</Typography>}
                      secondary={<Typography variant="caption">{p.total_qty} adet · {p.order_count} siparişte · ₺{Number(p.total_spent).toFixed(0)}</Typography>}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Button
            fullWidth variant="outlined" startIcon={<OpenInNewIcon />}
            onClick={() => { onClose(); navigate(`/customer/${userId}`); }}
          >
            Tam 360 Sayfasını Aç
          </Button>
        </Box>
      )}
    </Drawer>
  );
}

function StatCard({ label, value, subtitle, color }) {
  return (
    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, borderLeft: `3px solid ${color}` }}>
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{label}</Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 800, color }}>{value}</Typography>
      {subtitle && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{subtitle}</Typography>}
    </Box>
  );
}
